import { Paper, Typography, TextField, Button, IconButton, Box, MenuItem } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import type { PREducation } from '../../../types/salesforce'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface EducationsSectionProps {
  prSheetId?: string
  educations: PREducation[]
  onChange: (educations: PREducation[]) => void
  onBlur?: () => void
}

const STATUS_OPTIONS = ['入学', '卒業', '卒業予定'] as const

function SortableEducationItem({
  education,
  index,
  onDelete,
  onChange,
  onBlur,
}: {
  education: PREducation
  index: number
  onDelete: () => void
  onChange: (field: keyof PREducation, value: string) => void
  onBlur?: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: education.Id! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
            <DragIndicatorIcon />
          </IconButton>
          <Typography variant="subtitle2">学歴 #{index + 1}</Typography>
        </Box>
        <IconButton onClick={onDelete} size="small" color="error">
          <DeleteIcon />
        </IconButton>
      </Box>

      <TextField
        label="年"
        fullWidth
        margin="normal"
        type="number"
        value={education.Year__c || ''}
        onChange={(e) => {
          // 4桁以内に制限
          const value = e.target.value
          if (value === '' || (value.length <= 4 && /^\d+$/.test(value))) {
            onChange('Year__c', value)
          }
        }}
        onBlur={onBlur}
        placeholder="例: 2023"
        slotProps={{
          htmlInput: {
            min: 1900,
            max: 9999,
            step: 1,
            maxLength: 4,
          },
        }}
      />

      <TextField
        label="学校名"
        fullWidth
        margin="normal"
        value={education.SchoolName__c || ''}
        onChange={(e) => onChange('SchoolName__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="学部"
        fullWidth
        margin="normal"
        value={education.Department__c || ''}
        onChange={(e) => onChange('Department__c', e.target.value)}
        onBlur={onBlur}
      />

      <TextField
        label="ステータス"
        fullWidth
        margin="normal"
        select
        value={education.Status__c || '入学'}
        onChange={(e) => onChange('Status__c', e.target.value as (typeof STATUS_OPTIONS)[number])}
        onBlur={onBlur}
      >
        {STATUS_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  )
}

export function EducationsSection({ prSheetId, educations, onChange, onBlur }: EducationsSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const handleAdd = () => {
    const newEducation: PREducation = {
      Id: `temp-${Date.now()}`,
      PRSheet__c: prSheetId,
      Year__c: undefined,
      SchoolName__c: '',
      Department__c: '',
      Status__c: '入学',
      SortOrder__c: (educations.length + 1) * 10,
    }
    onChange([...educations, newEducation])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = educations.findIndex((edu) => edu.Id === active.id)
      const newIndex = educations.findIndex((edu) => edu.Id === over.id)

      const reorderedEducations = arrayMove(educations, oldIndex, newIndex)

      // SortOrder__cを更新
      const updatedEducations = reorderedEducations.map((edu, index) => ({
        ...edu,
        SortOrder__c: (index + 1) * 10,
      }))

      onChange(updatedEducations)
      if (onBlur) onBlur()
    }
  }

  const handleChange = (index: number, field: keyof PREducation, value: string) => {
    const newEducations = [...educations]
    // Year__cはnumber型なので変換
    if (field === 'Year__c') {
      newEducations[index] = {
        ...newEducations[index],
        [field]: value ? parseInt(value, 10) : undefined,
      }
    } else {
      newEducations[index] = {
        ...newEducations[index],
        [field]: value,
      }
    }
    onChange(newEducations)
  }

  const handleDelete = (index: number) => {
    const newEducations = educations.filter((_, i) => i !== index)
    onChange(newEducations)
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">🎓 学歴</Typography>
        <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
          追加
        </Button>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={educations.map((edu) => edu.Id!)} strategy={verticalListSortingStrategy}>
          {educations.map((education, index) => (
            <SortableEducationItem
              key={education.Id}
              education={education}
              index={index}
              onDelete={() => handleDelete(index)}
              onChange={(field, value) => handleChange(index, field, value)}
              onBlur={onBlur}
            />
          ))}
        </SortableContext>
      </DndContext>

      {educations.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          学歴を追加してください
        </Typography>
      )}
    </Paper>
  )
}
